import React, {useRef, useEffect} from 'react';
import {Button, RadioButton, Switch} from 'web-components';
import {useHistory} from 'react-router-dom';
import {BUDGET_TYPE, MODULE_TYPES, TASK_COST_CALCULATION_TYPE} from '../../../constants';
import {
	CheckBoxDescription,
	RadioWithDescriptionWrapper,
	SelectionElementLabel,
	SelectionElementLineWrapper,
	SubSection,
	FixedCostWrapper,
	FixedCostInput,
	Subtitle,
	RevenueChartWrapper,
} from './ProjectSettings_styled';
import {changeTaskCostCalculationType, changeUnassignedTaskHourlyCost, changeUseBaseline} from './ProjectSettingsMutations';
import {FormattedHTMLMessage} from 'react-intl';
import {CurrencyInputWrapper} from '../../../components/inputs/currency_input_wrapper';
import Util from '../../../forecast-app/shared/util/util';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import RateCardDropdown, {changeRateCard} from '../../../components/dropdowns/rate-card-dropdown/Rate_card_dropdown';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import UpdateProjectRevenueMarginMutation from '../../../mutations/UpdateProjectRevenueMarginMutation';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {BudgetThresholdsUpdateForm} from '../project_health/modals/forecast_surplus_modal/BudgetThresholdsUpdateForm';
import {ForecastSurplusModalChartMock} from '../project_health/modals/forecast_surplus_modal/ForecastSurplusModalChartMock';
import {hasModule} from '../../../forecast-app/shared/util/ModuleUtil';

export const TASK_MONITORING = {
	NO_TASK_MONITORING: 'no_monitoring',
	USE_FOLLOWERS: 'followers',
	USE_OWNER: 'owner',
};

const ProjectSettingsFinancials = ({
	intl,
	projectId,
	useBaseline,
	showChangeBudgetTypeModal,
	hasFinanceAccess,
	hasBaselineAccess,
	taskCostCalculationType,
	unassignedTaskHourlyCost,
	currency,
	projectDoneOrHalted,
	hasLockedPeriod,
	budgetType,
	hasFixedPriceV2,
	showUpdateFixedPriceModal,
	rateCard,
	company,
	project,
	projectStartDate,
	projectEndDate,
}) => {
	const hasNoTimeRegFeature = hasFeatureFlag('no_time_reg');
	const {formatMessage} = intl;
	const isDoneOrHalted = project.status === 'DONE' || project.status === 'HALTED';
	const isCompanyCurrency = currency === company.currency;
	const exchangeRate = isCompanyCurrency
		? 1
		: company.exchangeRates.edges.find(exchangeRate => exchangeRate.node.currency === currency).node.rate;

	const history = useHistory();
	const novaInsightsSectionRef = useRef(null);

	useEffect(() => {
		if (novaInsightsSectionRef.current && history.location.hash) {
			const section = history.location.hash.replace('#', '').split('+').pop();
			if (section && section.toLowerCase() === 'budget-tolerance') {
				novaInsightsSectionRef.current.scrollIntoView({block: 'end', inline: 'center'});
			}
		}
	}, []);

	const handleChangeFixedCost = (value, isProjectCurrency) => {
		if (isProjectCurrency) {
			value /= exchangeRate;
		}
		changeUnassignedTaskHourlyCost(project.id, value);
	};

	const updateProjectRevenueMargin = (revenueMarginLow, revenueMarginHigh) => {
		Util.CommitMutation(
			UpdateProjectRevenueMarginMutation,
			{id: projectId, revenueMarginLow: revenueMarginLow, revenueMarginHigh: revenueMarginHigh},
			() => createToast({duration: 3000, message: 'Revenue thresholds updated'})
		);
	};

	const hasNovaInsights = hasModule(MODULE_TYPES.NOVA_INSIGHTS) && hasFeatureFlag('project_health_page');

	return (
		<>
			{/** Baseline */}
			{budgetType !== BUDGET_TYPE.RETAINER && (
				<SubSection
					title={formatMessage({id: 'project_section.baseline'})}
					showCrown={!hasBaselineAccess}
					description={formatMessage({id: 'project_settings.baseline_desc'})}
				>
					<SelectionElementLineWrapper>
						<Switch
							checked={useBaseline}
							onChange={value => changeUseBaseline(projectId, value)}
							locked={projectDoneOrHalted || !hasBaselineAccess}
						/>
						<SelectionElementLabel>{formatMessage({id: 'common.enable'})}</SelectionElementLabel>
					</SelectionElementLineWrapper>
				</SubSection>
			)}
			{/** budget type */}
			{!hasNoTimeRegFeature && (
				<SubSection title={formatMessage({id: 'project_settings.project_budget_type'})}>
					{Util.getBudgetTypeTranslation(budgetType, intl)}
					<SelectionElementLineWrapper>
						<ForecastTooltip content={hasLockedPeriod && formatMessage({id: 'retainer.locked_period_disable'})}>
							<Button
								disabled={hasLockedPeriod}
								size={Button.SIZE.STANDARD}
								variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
								onClick={showChangeBudgetTypeModal}
								cy={'change-budget-type-button'}
							>
								{formatMessage({id: 'project_settings.change_budget_type'})}
							</Button>
						</ForecastTooltip>
					</SelectionElementLineWrapper>
				</SubSection>
			)}
			{/** rate card */}
			<SubSection title={formatMessage({id: 'settings.rate-card'})}>
				<div style={{width: '250px'}}>
					<RateCardDropdown
						rateCards={company.rateCards.edges}
						company={company}
						project={project}
						selectedItems={rateCard && rateCard.id ? [rateCard.id] : []}
						dropdownAlignment="none"
						onSelect={values => changeRateCard(projectId, values[0])}
						onRemove={() => false}
						disabled={isDoneOrHalted}
					/>
				</div>
			</SubSection>
			{/** Fixed Price V2 Upgrade */}
			{hasFixedPriceV2 && budgetType === BUDGET_TYPE.FIXED_PRICE ? (
				<SubSection
					title={formatMessage({id: 'project_budget.new_fixed_price_logic'})}
					actionElement={
						<Button
							size={Button.SIZE.STANDARD}
							variant={Button.VARIANT.PURPLE_OUTLINE}
							onClick={showUpdateFixedPriceModal}
						>
							{formatMessage({id: 'project_budget.update_fixed_price'})}
						</Button>
					}
				/>
			) : null}
			{/** Internal hourly cost calculation */}
			{hasFinanceAccess && (
				<SubSection
					title={formatMessage({id: 'project_settings.internal_hourly_cost_calculation'})}
					description={formatMessage({id: 'project_settings.internal_hourly_cost_calculation_desc'})}
				>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={
									taskCostCalculationType === TASK_COST_CALCULATION_TYPE.COMPANY_AVERAGE_UNASSIGNED_TASKS
								}
								onClick={() =>
									changeTaskCostCalculationType(
										projectId,
										TASK_COST_CALCULATION_TYPE.COMPANY_AVERAGE_UNASSIGNED_TASKS
									)
								}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.company_averages'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>
							{formatMessage({id: 'project_settings.company_averages_desc'})}
						</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={
									taskCostCalculationType === TASK_COST_CALCULATION_TYPE.PROJECT_TEAM_AVERAGE_UNASSIGNED_TASKS
								}
								onClick={() =>
									changeTaskCostCalculationType(
										projectId,
										TASK_COST_CALCULATION_TYPE.PROJECT_TEAM_AVERAGE_UNASSIGNED_TASKS
									)
								}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.project_team_averages'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>
							{formatMessage({id: 'project_settings.project_team_averages_desc'})}
						</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={taskCostCalculationType === TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS}
								onClick={() =>
									changeTaskCostCalculationType(projectId, TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS)
								}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.set_fixed_internal_cost'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>
							<FixedCostWrapper>
								<FixedCostInput>
									<CurrencyInputWrapper
										intl={intl}
										value={unassignedTaskHourlyCost}
										callback={value => handleChangeFixedCost(value, false)}
										currency={company.currency}
										width={'160px'}
										disabled={
											projectDoneOrHalted ||
											taskCostCalculationType !== TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS
										}
										label={`${intl.formatMessage({
											id: 'common.company_currency',
										})} (${company.currency})`}
									/>
								</FixedCostInput>
								<FixedCostInput>
									{!isCompanyCurrency && (
										<CurrencyInputWrapper
											intl={intl}
											value={unassignedTaskHourlyCost * exchangeRate}
											callback={value => handleChangeFixedCost(value, true)}
											currency={currency}
											width={'160px'}
											disabled={
												projectDoneOrHalted ||
												taskCostCalculationType !== TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS
											}
											label={`${intl.formatMessage({
												id: 'common.project_currency',
											})} (${currency})`}
										/>
									)}
								</FixedCostInput>
							</FixedCostWrapper>
						</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
				</SubSection>
			)}
			{budgetType === BUDGET_TYPE.FIXED_PRICE && hasNovaInsights ? (
				<SubSection title={'Set Budget Tolerance'} description={'This setting only applies to the current project.'}>
					{hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) ? (
						<Subtitle>
							{'You can change the company default tolerance in the '}
							<a href={'/settings/finance#budget-tolerance'}>admin settings</a>
						</Subtitle>
					) : null}
					<BudgetThresholdsUpdateForm
						revenueMarginLow={project.revenueMarginLow}
						revenueMarginHigh={project.revenueMarginHigh}
						callback={updateProjectRevenueMargin}
					/>
					<RevenueChartWrapper ref={novaInsightsSectionRef}>
						<ForecastSurplusModalChartMock
							fixedPrice={project.budget}
							startDate={projectStartDate}
							endDate={projectEndDate}
							currencySymbol={Util.GetCurrencySymbol(currency)}
							revenueMarginLow={project.revenueMarginLow}
							revenueMarginHigh={project.revenueMarginHigh}
						></ForecastSurplusModalChartMock>
					</RevenueChartWrapper>
				</SubSection>
			) : null}
		</>
	);
};
export default ProjectSettingsFinancials;
